'use client';

import React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { categoryShortLabel } from '../components/news/newsCategoryStyles';
import { NewsCategory } from '../types/news';

interface NewsArchiveClientProps {
  categories: string[];
  selectedCategory: string;
  currentPage: number;
  totalPages: number;
  showPagination?: boolean;
  variant?: 'tabs' | 'pills';
}

function getTabLabel(category: string): { mobile: string; desktop: string } {
  if (category === 'All') return { mobile: 'All', desktop: 'All' };
  const short = categoryShortLabel[category as NewsCategory];
  return { mobile: short ?? category, desktop: category };
}

const NewsArchiveClient: React.FC<NewsArchiveClientProps> = ({
  categories,
  selectedCategory,
  currentPage,
  totalPages,
  showPagination = false,
  variant = 'pills',
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const handleCategoryChange = (category: string) => {
    if (category === selectedCategory) return;

    const newParams = new URLSearchParams();
    newParams.set('page', '1');
    if (category !== 'All') {
      newParams.set('category', category);
    }
    router.push(`/news?${newParams.toString()}`);
  };

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber === currentPage) return;

    const validPageNumber = Math.min(Math.max(1, pageNumber), totalPages);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set('page', validPageNumber.toString());
    router.push(`/news?${newParams.toString()}`);
  };

  if (showPagination) {
    return (
      <div className="mt-8 flex justify-center sm:mt-12">
        <nav className="w-full max-w-xl" aria-label="News pagination">
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              className={`flex h-11 w-11 items-center justify-center rounded-full border ${
                currentPage === 1
                  ? 'cursor-not-allowed border-white/5 text-pink-200/25'
                  : 'cursor-pointer border-white/10 text-pink-100 active:bg-white/10 sm:hover:bg-white/5'
              }`}
              disabled={currentPage === 1}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <span className="min-w-[5rem] text-center text-sm font-medium text-pink-200/80">
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              className={`flex h-11 w-11 items-center justify-center rounded-full border ${
                currentPage === totalPages
                  ? 'cursor-not-allowed border-white/5 text-pink-200/25'
                  : 'cursor-pointer border-white/10 text-pink-100 active:bg-white/10 sm:hover:bg-white/5'
              }`}
              disabled={currentPage === totalPages}
              aria-label="Go to next page"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </div>
    );
  }

  if (variant === 'tabs') {
    return (
      <div className="scrollbar-hide -mx-4 flex gap-0.5 overflow-x-auto px-4 sm:mx-0 sm:gap-1 sm:px-0">
        {categories.map((category) => {
          const isActive = selectedCategory === category;
          const labels = getTabLabel(category);
          return (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`min-h-11 shrink-0 border-b-2 px-3 py-2.5 text-xs font-semibold transition sm:px-4 sm:py-3 sm:text-sm ${
                isActive
                  ? 'border-pink-400 text-white'
                  : 'border-transparent text-pink-200/55 active:text-pink-100 sm:hover:border-white/20 sm:hover:text-pink-100'
              }`}
            >
              <span className="sm:hidden">{labels.mobile}</span>
              <span className="hidden sm:inline">{labels.desktop}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2 md:justify-end">
      {categories.map((category) => (
        <button
          key={category}
          onClick={() => handleCategoryChange(category)}
          className={`min-h-10 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
            selectedCategory === category
              ? 'bg-pink-500/15 text-pink-200 ring-1 ring-pink-400/40'
              : 'text-pink-200/65 active:bg-charcoal-900/60 sm:hover:bg-charcoal-900/60 sm:hover:text-pink-100'
          }`}
        >
          {category}
        </button>
      ))}
    </div>
  );
};

export default NewsArchiveClient;
